import { ContentType } from "@brookbush/database";
import type { GlossaryTerm } from "@brookbush/types";

import Link from "next/link";
import { Fragment, useMemo } from "react";

import { AnnotationsProvider } from "~/components/Annotations";
import Comments from "~/components/Comments";
import ContentMembershipCTA from "~/components/ContentMembershipCTA";
import ExpressCard from "~/components/ExpressCard";
import TextBlock from "~/components/TextBlock";
import { getContentMembershipSplitIndices } from "~/lib/content/getMidContentSplitIndex";

const GlossaryTermBody = ({ glossaryTerm }: { glossaryTerm: GlossaryTerm }) => {
	const {
		_id,
		body,
		synonyms,
		relatedCourses,
		variations,
		title,
		relatedTerms,
	} = glossaryTerm;

	const blocks = body ?? [];
	const splitIndices = useMemo(
		() => getContentMembershipSplitIndices(blocks),
		[blocks],
	);
	const bodySegments = useMemo(() => {
		const segments = [];
		let start = 0;
		for (const index of splitIndices) {
			segments.push(blocks.slice(start, index));
			start = index;
		}
		segments.push(blocks.slice(start));
		return segments;
	}, [blocks, splitIndices]);

	return (
		<article className="mt-8 flex flex-col items-start gap-2 overflow-hidden sm:gap-8 lg:flex-row">
			<div className="flex w-full flex-col gap-3 sm:gap-6">
				<section className="rounded-2xl px-0 py-0 sm:border sm:px-8 sm:py-6 dark:bg-slate-900/20">
					<h2 className="mb-4 font-bold text-2xl">{title}</h2>

					<AnnotationsProvider
						contentId={_id}
						contentKey={_id}
						contentType={ContentType.GLOSSARY}
						askAI
						highlight
					>
						{bodySegments.map((segment, index) => (
							<Fragment key={`glossary-body-segment-${index}`}>
								{segment.length > 0 ? <TextBlock value={segment} /> : null}
								{index < splitIndices.length ? (
									<ContentMembershipCTA
										contentType={ContentType.GLOSSARY}
										contentId={_id}
										placementIndex={index}
									/>
								) : null}
							</Fragment>
						))}
					</AnnotationsProvider>
				</section>

				<section>
					<h3 className="mb-2 font-semibold text-xl">Discussion</h3>
					<Comments contentType={ContentType.GLOSSARY} contentId={_id} />
				</section>
			</div>

			<aside className="flex w-full shrink-0 flex-col gap-4 sm:w-96 sm:gap-8">
				<ExpressCard />

				<section className="flex flex-col gap-1 rounded-2xl border px-5 py-3 sm:gap-2 sm:px-6 sm:py-4 dark:bg-slate-900/20">
					<h3 className="font-semibold text-xl sm:text-2xl">Synonyms</h3>

					{synonyms && synonyms.length > 0 ? (
						<ol className="list-inside list-disc">
							{synonyms.map((synonym) => (
								<li key={synonym} className="text-lg">
									{synonym}
								</li>
							))}
						</ol>
					) : (
						<p>No synonyms available.</p>
					)}
				</section>

				<section className="flex flex-col gap-1 rounded-2xl border px-5 py-3 sm:gap-2 sm:px-6 sm:py-4 dark:bg-slate-900/20">
					<h3 className="font-semibold text-xl sm:text-2xl">Variations</h3>

					{variations && variations.length > 0 ? (
						<ol className="list-inside list-disc">
							{variations.map((variation) => (
								<li key={variation} className="text-lg">
									{variation}
								</li>
							))}
						</ol>
					) : (
						<p>No variations available.</p>
					)}
				</section>

				<section className="flex flex-col gap-1.5 rounded-2xl border px-5 py-3 sm:gap-2 sm:px-6 sm:py-4 dark:bg-slate-900/20">
					<h3 className="font-semibold text-xl sm:text-2xl">Related Terms</h3>

					{relatedTerms && relatedTerms.length > 0 ? (
						<ol className="list-inside list-disc">
							{relatedTerms.map((relatedTerm) => (
								<li key={relatedTerm} className="text-lg">
									{relatedTerm}
								</li>
							))}
						</ol>
					) : (
						<p>No related terms available.</p>
					)}
				</section>

				<section className="flex flex-col gap-1.5 rounded-2xl border px-5 py-3 sm:gap-2 sm:px-6 sm:py-4 dark:bg-slate-900/20">
					<h3 className="font-semibold text-xl sm:text-2xl">Related Courses</h3>

					{relatedCourses && relatedCourses.length > 0 ? (
						<nav className="flex flex-col gap-1.5 sm:gap-2">
							{relatedCourses.map((relatedCourse) => (
								<Link
									key={relatedCourse._id}
									prefetch={false}
									className="text-sky-700 hover:underline sm:text-lg dark:text-sky-300"
									href={`/courses/${relatedCourse.slug}`}
								>
									{relatedCourse.title}
								</Link>
							))}
						</nav>
					) : (
						<p>No related courses available.</p>
					)}
				</section>
			</aside>
		</article>
	);
};

export default GlossaryTermBody;
